import { action } from "@oclif/core/ux"
import * as fs from "node:fs/promises"
import path from "node:path"
import { spawnAndWait } from "./spawn-and-wait.js"

export type TemplatePreset = { name: string; description: string; thumbnail?: string; files?: string[] }

export interface Logger {
  error: (msg: string) => never
  log: (msg: string) => void
  warn: (msg: string) => void
}

export async function fileExists(p: string) {
  try {
    await fs.access(p)
    return true
  } catch {
    return false
  }
}

type FrameworkType = "angular" | "react"

type FrameworkConfig = {
  mainFileExt: string
  appEntryCandidates: string[]
  generateAppContent: (templateId: string) => string
}

const FRAMEWORK_CONFIGS: Record<FrameworkType, FrameworkConfig> = {
  angular: {
    mainFileExt: ".ts",
    appEntryCandidates: ["src/app.component.ts", "src/App.component.ts"],
    generateAppContent: (templateId: string) => {
      const componentClassName = `${templateId}Component`
      const selectorName = camelToKebab(templateId)
      return `import { Component } from "@angular/core";\nimport { ${componentClassName} } from "./components/${templateId}/${templateId}";\nimport { SnPrototypeComponent } from "../supernova/helpers/sn-prototype.component";\n\n@Component({\n  selector: "app-root",\n  standalone: true,\n  imports: [SnPrototypeComponent, ${componentClassName}],\n  template: \`<sn-prototype><${selectorName}></${selectorName}></sn-prototype>\`,\n})\nexport class AppComponent {}\n`
    },
  },
  react: {
    mainFileExt: ".tsx",
    appEntryCandidates: ["src/App.tsx", "src/app.tsx"],
    generateAppContent: (templateId: string) =>
      `import { ${templateId} } from "./components/${templateId}/${templateId}"\n\nexport default function App() {\n  return <${templateId} />\n}`,
  },
}

const FRAMEWORK_DETECTION_ORDER: FrameworkType[] = ["angular", "react"]

async function detectFrameworkAndEntryPath(): Promise<{ config: FrameworkConfig; appEntryPath: string }> {
  for (const framework of FRAMEWORK_DETECTION_ORDER) {
    const config = FRAMEWORK_CONFIGS[framework]
    for (const candidate of config.appEntryCandidates) {
      const resolved = path.resolve(candidate)
      if (await fileExists(resolved)) return { config, appEntryPath: resolved }
    }
  }
  // Fallback to react
  const config = FRAMEWORK_CONFIGS.react
  return { config, appEntryPath: path.resolve(config.appEntryCandidates[0]) }
}

function camelToKebab(str: string): string {
  return str.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase()
}

export async function validateTemplates(
  templates: { [key: string]: TemplatePreset },
  logger: Logger,
  debug?: boolean,
): Promise<void> {
  action.start(`🔍 Validating ${Object.keys(templates).length} template(s)`)

  const { config } = await detectFrameworkAndEntryPath()

  for (const [templateKey, template] of Object.entries(templates)) {
    action.start(`Validating template '${templateKey}'`)

    // Check if template has files
    if (!template.files || template.files.length === 0) {
      action.stop("no files specified")
      logger.error(`Template '${templateKey}': No files specified`)
    }

    // Validate main file exists
    const expectedMainFile = `supernova/templates/${templateKey}/${templateKey}${config.mainFileExt}`
    if (!template.files.includes(expectedMainFile)) {
      action.stop("main file missing")
      logger.error(`Template '${templateKey}': Expected main file '${expectedMainFile}' not found in files array`)
    }

    // Validate all files exist on disk
    for (const filePath of template.files) {
      if (!(await fileExists(path.resolve(filePath)))) {
        action.stop("files missing")
        logger.error(`Template '${templateKey}': File does not exist: ${filePath}`)
      }
    }

    // Actually test the template by copying and building
    try {
      await testTemplateBuild(templateKey, template.files, logger, debug)
      action.stop("build successful")
    } catch (error) {
      action.stop("build failed")
      logger.error(
        `Template '${templateKey}': Build test failed: ${error instanceof Error ? error.message : String(error)}`,
      )
    }
  }

  action.stop("validation complete")
}

async function testTemplateBuild(templateKey: string, files: string[], logger: Logger, debug?: boolean): Promise<void> {
  const { config, appEntryPath } = await detectFrameworkAndEntryPath()
  const componentsDir = path.resolve("src/components")
  const toDestPath = (filePath: string) =>
    path.join(componentsDir, filePath.replace(/^supernova\/(patterns|templates)\//, ""))

  let originalAppContent = ""
  let copiedFiles: string[] = []
  let failed = false

  try {
    await checkNoDirsExist(files.map(f => path.dirname(toDestPath(f))))
    copiedFiles = await copyFiles(files, toDestPath)
    originalAppContent = await patchAppEntry(appEntryPath, templateKey, config)
    await runBuild(logger)
  } catch (error) {
    failed = true
    throw error
  } finally {
    const keepFilesForDebugging = debug && failed
    if (!keepFilesForDebugging) {
      await cleanup(copiedFiles, appEntryPath, originalAppContent)
    }
  }
}

async function checkNoDirsExist(dirs: string[]): Promise<void> {
  for (const dir of new Set(dirs)) {
    if (await fileExists(dir)) {
      throw new Error(
        `Component directory already exists: ${path.relative(process.cwd(), dir)}. Please remove or rename this directory before testing the template.`,
      )
    }
  }
}

async function copyFiles(files: string[], toDestPath: (f: string) => string): Promise<string[]> {
  const copiedFiles: string[] = []
  for (const filePath of files) {
    const destPath = toDestPath(filePath)
    await fs.mkdir(path.dirname(destPath), { recursive: true })
    await fs.copyFile(path.resolve(filePath), destPath)
    copiedFiles.push(destPath)
    if (destPath.endsWith(".tsx") || destPath.endsWith(".ts")) {
      await updateImportsInFile(destPath)
    }
  }

  return copiedFiles
}

async function patchAppEntry(
  appEntryPath: string,
  templateKey: string,
  config: FrameworkConfig,
): Promise<string> {
  if (!(await fileExists(appEntryPath))) return ""
  const originalContent = await fs.readFile(appEntryPath, "utf8")
  await fs.writeFile(appEntryPath, config.generateAppContent(templateKey))
  return originalContent
}

async function runBuild(logger: Logger): Promise<void> {
  try {
    await spawnAndWait("npm", ["run", "build"], { cwd: process.cwd() })
  } catch (error) {
    logger.log(`Build failed with error: ${error instanceof Error ? error.message : String(error)}`)
    throw error
  }
}

async function cleanup(copiedFiles: string[], appTsxPath: string, originalAppContent: string): Promise<void> {
  for (const filePath of copiedFiles) {
    if (await fileExists(filePath)) {
      await fs.rm(filePath, { force: true })
    }
  }

  const sortedDirs = [...new Set(copiedFiles.map(f => path.dirname(f)))].sort((a, b) => b.length - a.length)
  for (const dir of sortedDirs) {
    try {
      await fs.rm(dir, { recursive: true, force: true })
    } catch {
      // ignore
    }
  }

  if (originalAppContent && (await fileExists(appTsxPath))) {
    await fs.writeFile(appTsxPath, originalAppContent)
  }
}

async function updateImportsInFile(filePath: string): Promise<void> {
  const content = await fs.readFile(filePath, "utf8")
  const updatedContent = content.replaceAll(
    /from\s+["']\.\.\/\.\.\/(patterns|templates)\/([^/"']+\/[^"']+)["']/g,
    'from "../$2"',
  )
  if (content !== updatedContent) {
    await fs.writeFile(filePath, updatedContent)
  }
}
